import * as React from "react";
import { Moon, Sun } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

// Logo imports from sources/originals
import IsotypeDay from "~/sources/originals/isotype_day.svg";
import IsotypeNight from "~/sources/originals/isotypo_night.svg";
import LogoTitleDay from "~/sources/originals/logo_title_day.svg";
import LogoTitleNight from "~/sources/originals/logo_title_night.svg";

export type Theme = "light" | "dark";

// Theme assets configuration
export const themeAssets = {
  light: {
    isotype: IsotypeDay,
    logoTitle: LogoTitleDay,
  },
  dark: {
    isotype: IsotypeNight,
    logoTitle: LogoTitleNight,
  },
} as const;

// Theme context for accessing current theme state
interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

export function useTheme() {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// Hook to get theme-aware logo
export function useThemeLogo() {
  const { theme } = useTheme();
  return themeAssets[theme];
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "secure-onboarding-theme",
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey) as Theme | null;
      if (stored === "light" || stored === "dark") {
        return stored;
      }
      // Default to system preference
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return defaultTheme;
  });

  const applyTheme = React.useCallback((newTheme: Theme) => {
    const root = document.documentElement;
    root.classList.toggle("dark", newTheme === "dark");
  }, []);

  React.useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const setTheme = React.useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(storageKey, newTheme);
  }, [storageKey]);

  const toggleTheme = React.useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  const value = React.useMemo(() => ({
    theme,
    setTheme,
    toggleTheme,
  }), [theme, setTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "relative h-9 w-9 rounded-full transition-colors",
        className
      )}
      onClick={toggleTheme}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      <Sun className={cn(
        "h-5 w-5 transition-all",
        isDark ? "scale-0 rotate-90 opacity-0" : "scale-100 rotate-0 opacity-100"
      )} />
      <Moon className={cn(
        "absolute h-5 w-5 transition-all",
        isDark ? "scale-100 rotate-0 opacity-100" : "scale-0 -rotate-90 opacity-0"
      )} />
      <span className="sr-only">
        {isDark ? "Switch to light mode" : "Switch to dark mode"}
      </span>
    </Button>
  );
}

// Theme-aware logo components
interface LogoProps {
  className?: string;
  alt?: string;
}

export function Isotype({ className, alt = "Logo" }: LogoProps) {
  const logos = useThemeLogo();
  return <img src={logos.isotype} alt={alt} className={className} />;
}

export function LogoTitle({ className, alt = "Logo" }: LogoProps) {
  const logos = useThemeLogo();
  return <img src={logos.logoTitle} alt={alt} className={className} />;
}
